import React, { useState, useEffect, useMemo } from 'react';
import {
  PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import { API_URL } from '../config';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { getAuthHeader } from '../utils/auth';
import CRMView from './CRMView';

// Date range options
const DATE_RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last_7_days', label: 'Last 7 Days' },
  { id: 'last_14_days', label: 'Last 14 Days' },
  { id: 'last_30_days', label: 'Last 30 Days' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
];

// Platform colors
const PLATFORM_COLORS = {
  META: '#1877F2',
  GOOGLE: '#4285F4'
};

// Platform Icon
const PlatformIcon = ({ platform, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  if (platform === 'META') {
    return (
      <div className={`${sizeClasses[size]} bg-[#1877F2] rounded-lg flex items-center justify-center`}>
        <svg className="w-3/5 h-3/5 text-white" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </div>
    );
  }
  
  return (
    <div className={`${sizeClasses[size]} bg-white rounded-lg flex items-center justify-center`}>
      <svg className="w-3/5 h-3/5" viewBox="0 0 24 24" fill="none">
        <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4" />
        <path d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z" fill="#34A853" />
        <path d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC05" />
        <path d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.0344664 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z" fill="#EA4335" />
      </svg>
    </div>
  );
};

// Date Range Selector
const DateRangeSelector = ({ selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2.5 bg-[#18181B] border border-white/10 rounded-xl text-sm font-medium hover:border-[#6366f1]/50 transition-colors"
      >
        <svg className="w-4 h-4 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="text-white">{DATE_RANGES.find(r => r.id === selected)?.label}</span>
        <svg className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-48 bg-[#18181B] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
            <div className="p-2">
              {DATE_RANGES.map(range => (
                <button
                  key={range.id}
                  onClick={() => { onChange(range.id); setIsOpen(false); }}
                  className={`w-full px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                    selected === range.id
                      ? 'bg-[#6366f1]/20 text-[#22c55e]'
                      : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Platform Filter Component
const PlatformFilter = ({ selected, onChange, availablePlatforms }) => {
  // Build filter options based on available platforms
  const filterOptions = useMemo(() => {
    const options = [{ id: 'all', label: 'All Platforms', color: null }];
    
    if (availablePlatforms.includes('META')) {
      options.push({ id: 'META', label: 'Meta Ads', color: '#1877F2' });
    }
    if (availablePlatforms.includes('GOOGLE')) {
      options.push({ id: 'GOOGLE', label: 'Google Ads', color: '#4285F4' });
    }
    
    return options;
  }, [availablePlatforms]);
  
  // Don't show filter if only one platform available
  if (filterOptions.length <= 2) {
    return null;
  }
  
  return (
    <div className="flex items-center gap-1 bg-[#18181B] border border-white/10 rounded-xl p-1">
      {filterOptions.map(option => (
        <button
          key={option.id}
          onClick={() => onChange(option.id)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            selected === option.id
              ? 'bg-[#6366f1]/20 text-[#22c55e]'
              : 'text-zinc-400 hover:text-white hover:bg-white/5'
          }`}
        >
          {option.color && (
            <div 
              className="w-2.5 h-2.5 rounded-full" 
              style={{ backgroundColor: option.color }}
            />
          )}
          {option.label}
        </button>
      ))}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, subValue, color = 'default', trend }) => {
  const colors = {
    default: 'from-white/5 to-transparent border-white/10',
    teal: 'from-[#6366f1]/10 to-transparent border-[#6366f1]/30',
    mint: 'from-[#22c55e]/10 to-transparent border-[#22c55e]/30',
    orange: 'from-[#FF6826]/10 to-transparent border-[#FF6826]/30',
  };

  const valueColors = {
    default: 'text-white',
    teal: 'text-[#6366f1]',
    mint: 'text-[#22c55e]',
    orange: 'text-[#FF6826]',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-2xl p-5 hover:scale-[1.02] transition-transform`}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-zinc-400">
          {icon}
        </div>
        {trend !== undefined && (
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${trend >= 0 ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'bg-red-500/20 text-red-400'}`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
      <p className={`text-3xl font-bold font-mono ${valueColors[color]}`}>{value}</p>
      <p className="text-sm text-zinc-400 mt-1">{label}</p>
      {subValue && <p className="text-xs text-zinc-500 mt-0.5">{subValue}</p>}
    </div>
  );
};

// Account Card Component
const AccountCard = ({ account, isExpanded, onToggle }) => {
  const totalResults = account.total_results || (account.leads + account.messaging_conversations);
  
  return (
    <div className={`bg-[#09090B] rounded-2xl border transition-colors ${
      isExpanded ? 'border-[#6366f1]/50 ring-2 ring-[#6366f1]/20' : 'border-white/10 hover:border-white/20'
    }`}>
      {/* Account Header */}
      <div className="p-5 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <PlatformIcon platform={account.platform} size="lg" />
            <div>
              <h3 className="font-bold text-white text-lg">{account.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                  account.platform === 'META' ? 'bg-[#1877F2]/20 text-[#1877F2]' : 'bg-white/10 text-white'
                }`}>
                  {account.platform}
                </span>
                <span className="text-xs text-zinc-500">{account.campaigns?.length || 0} campaigns</span>
                {/* Lead Sheet Badge */}
                {account.lead_sheet && (
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-[#34A853]/20 text-[#34A853]">
                    Lead Sheet
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="text-right hidden sm:block">
              <p className="text-xs text-zinc-500 uppercase">Spend</p>
              <p className="text-xl font-bold text-white font-mono">{formatCurrency(account.spend)}</p>
            </div>
            <div className="text-right hidden sm:block">
              <p className="text-xs text-zinc-500 uppercase">Results</p>
              <p className="text-xl font-bold text-[#22c55e] font-mono">{formatNumber(totalResults)}</p>
            </div>
            <div className="text-right hidden md:block">
              <p className="text-xs text-zinc-500 uppercase">CPL</p>
              <p className="text-xl font-bold text-white font-mono">{formatCurrency(account.cpl)}</p>
            </div>
            {/* View Leads Button */}
            {account.lead_sheet && (
              <a
                href={account.lead_sheet.spreadsheet_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="hidden md:flex items-center gap-2 px-3 py-2 bg-[#34A853]/10 hover:bg-[#34A853]/20 rounded-lg text-xs text-[#34A853] font-medium transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.385 2H4.615A2.615 2.615 0 002 4.615v14.77A2.615 2.615 0 004.615 22h14.77A2.615 2.615 0 0022 19.385V4.615A2.615 2.615 0 0019.385 2zM8 18H5v-3h3v3zm0-5H5v-3h3v3zm0-5H5V5h3v3zm11 10h-9v-3h9v3zm0-5h-9v-3h9v3zm0-5h-9V5h9v3z"/>
                </svg>
                View Leads
              </a>
            )}
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isExpanded ? 'bg-[#6366f1]/20 text-[#6366f1]' : 'bg-white/5 text-zinc-500'
            }`}>
              <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Mobile stats */}
        <div className="grid grid-cols-3 gap-2 mt-4 sm:hidden">
          <div className="bg-[#18181B] rounded-lg p-2 text-center">
            <p className="text-xs text-zinc-500">Spend</p>
            <p className="text-sm font-bold text-white font-mono">{formatCurrency(account.spend)}</p>
          </div>
          <div className="bg-[#18181B] rounded-lg p-2 text-center">
            <p className="text-xs text-zinc-500">Results</p>
            <p className="text-sm font-bold text-[#22c55e] font-mono">{formatNumber(totalResults)}</p>
          </div>
          <div className="bg-[#18181B] rounded-lg p-2 text-center">
            <p className="text-xs text-zinc-500">CPL</p>
            <p className="text-sm font-bold text-white font-mono">{formatCurrency(account.cpl)}</p>
          </div>
        </div>
        
        {/* Mobile View Leads Button */}
        {account.lead_sheet && (
          <a
            href={account.lead_sheet.spreadsheet_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex md:hidden items-center justify-center gap-2 mt-3 px-3 py-2.5 bg-[#34A853]/10 hover:bg-[#34A853]/20 rounded-lg text-xs text-[#34A853] font-medium transition-colors w-full"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.385 2H4.615A2.615 2.615 0 002 4.615v14.77A2.615 2.615 0 004.615 22h14.77A2.615 2.615 0 0022 19.385V4.615A2.615 2.615 0 0019.385 2zM8 18H5v-3h3v3zm0-5H5v-3h3v3zm0-5H5V5h3v3zm11 10h-9v-3h9v3zm0-5h-9v-3h9v3zm0-5h-9V5h9v3z"/>
            </svg>
            View Leads in Google Sheets
          </a>
        )}
      </div>
      
      {/* Campaigns List */}
      {isExpanded && account.campaigns && account.campaigns.length > 0 && (
        <div className="border-t border-white/10 p-5 space-y-3 max-h-[400px] overflow-y-auto">
          <h4 className="text-sm font-semibold text-zinc-400 mb-3">Campaign Performance</h4>
          {account.campaigns.map((campaign, idx) => (
            <div key={campaign.id || idx} className="bg-[#0D0D0F] rounded-xl p-4 hover:bg-[#111113] transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h5 className="text-sm font-medium text-white truncate">{campaign.name}</h5>
                    {/* Paused Badge */}
                    {campaign.is_paused && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-yellow-500/20 text-yellow-400 flex-shrink-0">
                        Paused
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                    <span>CTR {campaign.ctr}%</span>
                    <span>•</span>
                    <span>{formatNumber(campaign.clicks)} clicks</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-right shrink-0">
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Results</p>
                    <p className="text-lg font-bold text-[#22c55e] font-mono">{formatNumber(campaign.results)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">CPL</p>
                    <p className="text-lg font-bold text-white font-mono">{formatCurrency(campaign.cpl)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase">Spend</p>
                    <p className="text-lg font-bold text-white font-mono">{formatCurrency(campaign.spend)}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className="bg-[#18181B] border border-white/10 rounded-xl p-3 shadow-xl">
      <p className="text-xs text-zinc-400 mb-2">{label}</p>
      {payload.map((entry, idx) => (
        <p key={idx} className="text-sm font-medium" style={{ color: entry.color }}>
          {entry.name}: {entry.name.includes('Spend') || entry.name.includes('CPL') 
            ? formatCurrency(entry.value) 
            : formatNumber(entry.value)}
        </p>
      ))}
    </div>
  );
};

// Main Client Dashboard Component
const ClientDashboard = ({ clientId, token, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('last_7_days');
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [activeChartTab, setActiveChartTab] = useState('spend');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [portalAccess, setPortalAccess] = useState({ sheet_enabled: true, crm_enabled: false, crm_read_only: false, is_paused: false });
  const [activeMode, setActiveMode] = useState('sheet'); // 'sheet' | 'crm'

  // Fetch portal access (for logged-in clients only)
  useEffect(() => {
    if (!clientId || token) return;
    fetch(`${API_URL}/client/portal-access`, { headers: getAuthHeader() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setPortalAccess(d);
          // Default to CRM if sheet is disabled
          if (!d.sheet_enabled && d.crm_enabled) setActiveMode('crm');
        }
      })
      .catch(() => {});
  }, [clientId, token]);

  // Fetch dashboard data
  const fetchData = async () => {
    setLoading(true);
    try {
      let url;
      let fetchOptions = {};

      if (token) {
        // Share-link access — token in URL, no auth header needed
        url = `${API_URL}/client/dashboard/token/${token}?date_range=${dateRange}`;
      } else if (clientId) {
        // Logged-in client access — JWT in Authorization header
        url = `${API_URL}/client/dashboard?date_range=${dateRange}`;
        fetchOptions = { headers: getAuthHeader() };
      } else {
        throw new Error('No authentication provided');
      }

      const response = await fetch(url, fetchOptions);
      if (!response.ok) {
        throw new Error('Failed to load dashboard data');
      }

      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token, dateRange]);

  // Calculate platform breakdown
  const platformData = useMemo(() => {
    if (!data?.accounts) return [];
    
    const metaAccounts = data.accounts.filter(a => a.platform === 'META');
    const googleAccounts = data.accounts.filter(a => a.platform === 'GOOGLE');
    
    return [
      {
        name: 'Meta Ads',
        spend: metaAccounts.reduce((sum, a) => sum + a.spend, 0),
        leads: metaAccounts.reduce((sum, a) => sum + a.total_results, 0),
        accounts: metaAccounts.length,
        color: PLATFORM_COLORS.META
      },
      {
        name: 'Google Ads',
        spend: googleAccounts.reduce((sum, a) => sum + a.spend, 0),
        leads: googleAccounts.reduce((sum, a) => sum + a.total_results, 0),
        accounts: googleAccounts.length,
        color: PLATFORM_COLORS.GOOGLE
      }
    ].filter(p => p.spend > 0);
  }, [data]);

  // Get available platforms
  const availablePlatforms = useMemo(() => {
    return data?.available_platforms || [];
  }, [data]);

  // Prepare chart data with platform filtering
  const chartData = useMemo(() => {
    if (!data?.trend_data) return [];
    return data.trend_data.map(d => {
      // Determine which values to use based on platform filter
      let spend, leads, cpl;
      
      if (platformFilter === 'META') {
        spend = d.meta_spend || 0;
        leads = d.meta_leads || 0;
        cpl = d.meta_cpl || 0;
      } else if (platformFilter === 'GOOGLE') {
        spend = d.google_spend || 0;
        leads = d.google_leads || 0;
        cpl = d.google_cpl || 0;
      } else {
        // All platforms
        spend = d.spend || 0;
        leads = d.leads || 0;
        cpl = d.cpl || 0;
      }
      
      return {
        ...d,
        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        spend,
        leads,
        cpl,
        clicks: d.clicks || 0,
        impressions: d.impressions || 0
      };
    });
  }, [data, platformFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#02040A]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#6366f1]/20 rounded-full animate-spin border-t-[#6366f1] mx-auto"></div>
          <p className="text-zinc-500 mt-4 text-sm">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#02040A]">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Unable to Load Dashboard</h2>
          <p className="text-zinc-400 mb-6">{error}</p>
          <button
            onClick={fetchData}
            className="px-6 py-3 bg-[#6366f1] text-white font-bold rounded-xl hover:bg-[#5558e8] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!clientId && !token) return null;

  return (
    <div className="min-h-screen bg-[#02040A] text-white">
      {/* Background */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(99,102,241,0.08)_0%,transparent_50%),radial-gradient(circle_at_90%_80%,rgba(34,197,94,0.05)_0%,transparent_50%)] pointer-events-none" />
      
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#02040A]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="Signal" className="h-10 w-auto" onError={(e) => e.target.style.display='none'} />
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-white">
                  {data?.client?.company_name || data?.link_name || 'Client Dashboard'}
                </h1>
                <p className="text-xs text-zinc-500">Performance Overview</p>
              </div>
            </div>

            {/* Mode tabs — only shown when both modes are available */}
            {clientId && !token && (portalAccess.sheet_enabled && portalAccess.crm_enabled) && (
              <div className="flex bg-black/40 border border-white/10 rounded-xl p-1 gap-1">
                <button
                  onClick={() => setActiveMode('sheet')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                    activeMode === 'sheet' ? 'bg-[#6366f1]/20 text-[#6366f1]' : 'text-zinc-500 hover:text-white'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Ad Reports
                </button>
                <button
                  onClick={() => setActiveMode('crm')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                    activeMode === 'crm' ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'text-zinc-500 hover:text-white'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  CRM
                </button>
              </div>
            )}

            <div className="flex items-center gap-3">
              {activeMode === 'sheet' && <DateRangeSelector selected={dateRange} onChange={setDateRange} />}
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="p-2 rounded-xl text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                  title="Sign Out"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* CRM View — full portal when CRM mode active */}
      {clientId && !token && activeMode === 'crm' && portalAccess.crm_enabled && (
        <main className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
          {portalAccess.is_paused ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Portal Paused</h2>
              <p className="text-zinc-400 text-sm">{portalAccess.pause_reason || 'Contact your account manager.'}</p>
            </div>
          ) : (
            <CRMView readOnly={portalAccess.crm_read_only} />
          )}
        </main>
      )}

      {/* Sheet View (ad reports) — shown by default or when sheet mode active */}
      {(activeMode === 'sheet' || !clientId || token) && (
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8 space-y-8">
        {/* Paused banner — visible on page load regardless of mode */}
        {clientId && !token && portalAccess.is_paused && (
          <div className="flex items-center gap-4 bg-orange-500/10 border border-orange-500/30 rounded-2xl px-5 py-4">
            <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-orange-400 text-sm">Portal Access Paused</p>
              <p className="text-orange-300/70 text-xs mt-0.5">{portalAccess.pause_reason || 'Contact your account manager for details.'}</p>
            </div>
          </div>
        )}
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            label="Total Spend"
            value={formatCurrency(data?.summary?.total_spend)}
            subValue={`${data?.summary?.meta_accounts || 0} Meta + ${data?.summary?.google_accounts || 0} Google`}
            color="teal"
          />
          <StatCard
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
            label="Total Leads"
            value={formatNumber(data?.summary?.total_leads)}
            subValue={`From ${data?.summary?.total_accounts || 0} accounts`}
            color="mint"
          />
          <StatCard
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>}
            label="Avg. CPL"
            value={formatCurrency(data?.summary?.avg_cpl)}
            subValue="Cost per lead"
            color="default"
          />
          <StatCard
            icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>}
            label="Avg. CTR"
            value={`${data?.summary?.avg_ctr?.toFixed(2) || 0}%`}
            subValue={`${formatNumber(data?.summary?.total_clicks)} clicks`}
            color="orange"
          />
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Trend Chart - Larger */}
          <div className="lg:col-span-2 bg-[#09090B] rounded-2xl border border-white/10 p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h3 className="font-bold text-white">Performance Trend</h3>
              <div className="flex flex-wrap items-center gap-3">
                {/* Platform Filter - Dynamic */}
                <PlatformFilter 
                  selected={platformFilter}
                  onChange={setPlatformFilter}
                  availablePlatforms={availablePlatforms}
                />
                {/* Metric Tabs */}
                <div className="flex gap-1 bg-[#18181B] border border-white/10 rounded-xl p-1">
                  {['spend', 'leads', 'cpl'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveChartTab(tab)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        activeChartTab === tab
                          ? 'bg-[#6366f1]/20 text-[#22c55e]'
                          : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
                  <YAxis stroke="#71717A" fontSize={11} tickFormatter={(v) => activeChartTab === 'spend' || activeChartTab === 'cpl' ? `₹${(v/1000).toFixed(0)}K` : v} />
                  <Tooltip content={<CustomTooltip />} />
                  {activeChartTab === 'spend' && (
                    <Area type="monotone" dataKey="spend" stroke="#6366f1" strokeWidth={2} fill="url(#colorSpend)" name="Spend" />
                  )}
                  {activeChartTab === 'leads' && (
                    <Area type="monotone" dataKey="leads" stroke="#22c55e" strokeWidth={2} fill="url(#colorLeads)" name="Leads" />
                  )}
                  {activeChartTab === 'cpl' && (
                    <Area type="monotone" dataKey="cpl" stroke="#FF6826" strokeWidth={2} fill="url(#colorSpend)" name="CPL" />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-zinc-500">
                No data available for this period
              </div>
            )}
          </div>

          {/* Platform Breakdown */}
          <div className="bg-[#09090B] rounded-2xl border border-white/10 p-6">
            <h3 className="font-bold text-white mb-6">Platform Breakdown</h3>
            
            {platformData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={platformData}
                      dataKey="spend"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={75}
                      paddingAngle={5}
                    >
                      {platformData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                
                <div className="space-y-3 mt-4">
                  {platformData.map((platform, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: platform.color }} />
                        <span className="text-sm text-zinc-300">{platform.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-white">{formatCurrency(platform.spend)}</p>
                        <p className="text-xs text-zinc-500">{platform.leads} leads</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-zinc-500">
                No platform data
              </div>
            )}
          </div>
        </div>

        {/* Account Performance */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">Account Performance</h3>
            <span className="text-sm text-zinc-500">{data?.accounts?.length || 0} accounts</span>
          </div>
          
          {data?.accounts && data.accounts.length > 0 ? (
            <div className="space-y-4">
              {data.accounts.map(account => (
                <AccountCard
                  key={account.id}
                  account={account}
                  isExpanded={expandedAccountId === account.id}
                  onToggle={() => setExpandedAccountId(expandedAccountId === account.id ? null : account.id)}
                />
              ))}
            </div>
          ) : (
            <div className="bg-[#09090B] rounded-2xl border border-white/10 p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-zinc-700 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-zinc-500">No account data available</p>
              <p className="text-xs text-zinc-600 mt-1">Contact your account manager for access</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center py-8 border-t border-white/5">
          <p className="text-xs text-zinc-600">
            Powered by <span className="text-[#6366f1]">Profitcast Signal</span> • Real-time Analytics
          </p>
        </div>
      </main>
      )}
    </div>
  );
};

export default ClientDashboard;
